import { Grid, InputAdornment, FormControlLabel, Checkbox, Slider } from "@mui/material"
import styles from "../../Styles/ComponentStyles/InflationCalculator/InflationCalculator.module.scss"
import { useContext, useEffect, useState } from "react"
import HistoricalCpiChart from "./HistoricalCpiChart"
import FutureValueChart from "./FutureValueChart"
import { ChartData } from "chart.js";
import agent from "../../service/api"
import { UserContext, UserContextPayload } from "../../Context/UserDetailContext"
import GlobalButton from "../../Utils/GlobalUtilsComponents/GlobalButton"
import CpiCategoryData from "./CpiCategoryData"
import GlobalTextfield from "../../GlobalComponent/GlobalTextfield/GlobalTextfield"
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface HistoricalDataPoint {
    year: number;
    rate: number;
}

interface cpiDataInterface {
    bls_cpi_category_change_id: number;
    category_name: string;
    reference_date: string;
    percent_change: string;
    created_at: string;
    updated_on: string;
    flag_deleted: boolean;
    selected: boolean;
}

const InflationCalculator = () => {
    // const { showSnackbar } = useSnackbar();

    const {
        setFlagLoader,
    } = useContext(UserContext) as UserContextPayload

    // Controlled input values
    const [inputAmount, setInputAmount] = useState<number | undefined>(undefined);
    const [inputRate, setInputRate] = useState<string>("");
    const [inputYears, setInputYears] = useState<number | undefined>(undefined);
    const [useHistoricalRates, setUseHistoricalRates] = useState<boolean>(false);

    // Values used for calculation/chart
    const [amount, setAmount] = useState<number | undefined>(undefined);
    const [rate, setRate] = useState<string>("");
    const [years, setYears] = useState<number | undefined>(undefined);
    const [calculatedUseHistoricalRates, setCalculatedUseHistoricalRates] = useState<boolean>(false);

    const [result, setResult] = useState<string>("")
    const [historicalData, setHistoricalData] = useState<HistoricalDataPoint[]>([]);
    const [cpiData, setCpiData] = useState<cpiDataInterface[]>([]);
    const [showChart, setShowChart] = useState<boolean>(false)
    const [chartData, setChartData] = useState<ChartData<"line">>({ labels: [], datasets: [] });
    const [blsSyncDate, setBlsSyncDate] = useState("")

    // Fetch historical inflation data
    useEffect(() => {
        fetchHistoricalData();
        fetchCpiCategoryChangesData();
    }, []);

    const fetchHistoricalData = async () => {
        try {
            setFlagLoader(true)
            const response = await agent.getHistoricalInflationRateAPI.get();
            setHistoricalData(response?.data);
        } catch (error) {
            console.error('Error fetching historical data:', error);
            // You might want to show an error message to the user here
        } finally {
            setFlagLoader(false)
        }
    };

    const fetchCpiCategoryChangesData = async () => {
        try {
            setFlagLoader(true)
            const response = await agent.getCpiCategoryChanges.get();
            setCpiData(response?.data);
            setBlsSyncDate(response?.bls_sync_date)
        } catch (error) {
            console.error('Error fetching historical data:', error);
            // You might want to show an error message to the user here
        } finally {
            setFlagLoader(false)
        }
    };

    // Helper to format numbers with thousand separators
    const formatNumberWithCommas = (value: number | string | undefined) => {
        if (value === null || value === undefined) return '';
        const parts = value.toString().split('.')
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.')
    }

    // Calculate button enabled/disabled state
    const isCalculateEnabled = () => {
        if (inputAmount === undefined) return false;
        if (inputYears === undefined || inputYears == 0) return false;
        if (!useHistoricalRates && inputRate === "") return false;
        if (useHistoricalRates && historicalData.length > 0 && inputYears > historicalData.length - 1) return false;
        return true;
    };

    // Helper function to get max time period
    const getMaxTimePeriod = () => {
        const maxFromData = useHistoricalRates ? Math.max(1, historicalData.length - 1) : 50;
        return Math.min(50, maxFromData); // UI is designed for up to 50 years
    };

    // Calculate inflation and update chart data
    const calculateInflation = () => {
        if (!isCalculateEnabled()) return;

        // Copy input values to calculation states
        setAmount(inputAmount);
        setRate(inputRate);
        setYears(inputYears);
        setCalculatedUseHistoricalRates(useHistoricalRates);

        let currentValue = inputAmount ?? 0;
        const r = parseFloat(inputRate) / 100;
        const y = inputYears ?? 0;
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - y;

        const erosionValues: number[] = [];
        const yearLabels: string[] = [];
        const usedRates: number[] = [];

        // Add initial value
        erosionValues.push(Math.round(currentValue * 100) / 100);
        yearLabels.push(`Jan ${startYear}`);
        usedRates.push(0);

        if (useHistoricalRates) {
            // Use historical rates for calculation
            const relevantHistoricalData = historicalData
                .filter(item => item.year >= startYear && item.year <= currentYear)
                .sort((a, b) => a.year - b.year);

            for (let i = 1; i <= y; i++) {
                const year = startYear + i;
                const historicalRate = relevantHistoricalData.find(item => item.year === year - 1)?.rate || 0;
                currentValue = currentValue / (1 + historicalRate / 100);
                currentValue = Math.round(currentValue * 100) / 100;
                erosionValues.push(currentValue);
                yearLabels.push(`Jan ${year}`);
                usedRates.push(historicalRate);
            }
        } else {
            // Use static rate for calculation
            for (let i = 1; i <= y; i++) {
                currentValue = currentValue / (1 + r);
                currentValue = Math.round(currentValue * 100) / 100;
                erosionValues.push(currentValue);
                yearLabels.push(`Jan ${startYear + i}`);
                usedRates.push(r * 100); // Convert back to percentage for display
            }
        }

        setResult(`$${erosionValues[erosionValues.length - 1].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

        setChartData({
            labels: yearLabels,
            datasets: [
                {
                    label: 'Erosion of Buying Power',
                    data: erosionValues,
                    borderColor: useHistoricalRates ? '#FF6B6B' : '#7086FD',
                    backgroundColor: useHistoricalRates ? 'rgba(255, 107, 107, 0.1)' : 'rgba(112, 134, 253, 0.1)',
                    fill: true,
                    tension: 0,
                    pointBackgroundColor: useHistoricalRates ? '#FF6B6B' : '#7086FD',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 1,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    pointHoverBackgroundColor: useHistoricalRates ? '#FF6B6B' : '#7086FD',
                    pointHoverBorderColor: '#ffffff',
                    pointHoverBorderWidth: 2,
                },
            ],
        });

        setShowChart(true);
    };

    const parseCurrencyToNumber = (value: string) => {
        const n = Number(value.replace(/[^0-9.]+/g, ""));
        return Number.isFinite(n) ? n : 0;
    };

    const formatMoney = (value: number) =>
        `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    return (
        <Grid container gap={2} padding={"20px"} className={styles.inflationCalculatorLayout}>
            <Grid item xs={3.5} className={styles.leftScrollSection}>
                <Grid container className={styles.leftPanelCard}>
                    <Grid item xs={12} className={styles.sectionTitle}>
                        Calculator Inputs
                    </Grid>

                    <Grid item xs={12} className={styles.inputBlock}>
                        <div className={styles.inputHeaderRow}>
                            <span className={styles.inputLabel}>Initial Amount</span>
                            <div className={styles.valuePillWrap}>
                                <GlobalTextfield
                                    variant="outlined"
                                    size="small"
                                    fullWidth={false}
                                    autoComplete="off"
                                    stylingClassName={styles.valuePill}
                                    value={formatNumberWithCommas(inputAmount)}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, '');
                                        if (raw === '' || /^\d+$/.test(raw)) {
                                            const next = raw === '' ? undefined : Number(raw);
                                            setInputAmount(next);
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" className={styles.valuePillAdornment}>
                                                $
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>

                        <Slider
                            className={styles.slider}
                            size="small"
                            value={typeof inputAmount === 'number' ? inputAmount : 0}
                            min={0}
                            max={5000000}
                            step={10000}
                            onChange={(_, v) => setInputAmount(Array.isArray(v) ? v[0] : v)}
                        />
                        <div className={styles.sliderLabels}>
                            <span>$1K</span>
                            <span>$5M</span>
                        </div>
                    </Grid>

                    <Grid item xs={12} className={styles.inputBlock}>
                        <div className={styles.inputHeaderRow}>
                            <span className={styles.inputLabel}>Inflation Rate</span>
                            <div className={styles.valuePillWrap}>
                                <GlobalTextfield
                                    variant="outlined"
                                    size="small"
                                    fullWidth={false}
                                    autoComplete="off"
                                    stylingClassName={styles.valuePill}
                                    value={inputRate}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                            setInputRate(value);
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" className={styles.valuePillAdornment}>
                                                %
                                            </InputAdornment>
                                        ),
                                    }}
                                    disabled={useHistoricalRates}
                                />
                            </div>
                        </div>

                        <Slider
                            className={styles.slider}
                            size="small"
                            value={Number.isFinite(Number(inputRate)) ? Number(inputRate) : 0}
                            min={0}
                            max={20}
                            step={0.1}
                            onChange={(_, v) => setInputRate(String(Array.isArray(v) ? v[0] : v))}
                            disabled={useHistoricalRates}
                        />
                        <div className={styles.sliderLabels}>
                            <span>0%</span>
                            <span>20%</span>
                        </div>

                        <FormControlLabel
                            className={styles.checkboxRow}
                            control={
                                <Checkbox
                                    checked={useHistoricalRates}
                                    onChange={(e) => setUseHistoricalRates(e.target.checked)}
                                />
                            }
                            label="Use historical rates instead"
                        />
                    </Grid>

                    <Grid item xs={12} className={styles.inputBlock}>
                        <div className={styles.inputHeaderRow}>
                            <span className={styles.inputLabel}>Time Period</span>
                            <div className={styles.valuePillWrap}>
                                <GlobalTextfield
                                    variant="outlined"
                                    size="small"
                                    fullWidth={false}
                                    autoComplete="off"
                                    stylingClassName={styles.valuePill}
                                    value={inputYears ?? ''}
                                    onChange={(e) => {
                                        const sanitized = e.target.value.replace(/\D/g, '');
                                        const value = sanitized === '' ? undefined : Number(sanitized);
                                        const maxYears = getMaxTimePeriod();
                                        if (value === undefined) {
                                            setInputYears(undefined);
                                            return;
                                        }
                                        if (value >= 1 && value <= maxYears) setInputYears(value);
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <span className={styles.valuePillAdornment}>years</span>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>

                        <Slider
                            className={styles.slider}
                            size="small"
                            value={typeof inputYears === 'number' ? inputYears : 1}
                            min={1}
                            max={getMaxTimePeriod()}
                            step={1}
                            onChange={(_, v) => setInputYears(Array.isArray(v) ? v[0] : v)}
                        />
                        <div className={styles.sliderLabels}>
                            <span>1yr</span>
                            <span>50yrs</span>
                        </div>
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <GlobalButton
                            value="Calculate"
                            onClick={calculateInflation}
                            disabled={!isCalculateEnabled()}
                            className={styles.calculateBtnFull}
                            sx={{
                                "&.Mui-disabled": { opacity: "0.6 !important" }
                            }}
                        />
                    </Grid>

                    {showChart && amount !== undefined && years !== undefined && (
                        <Grid item xs={12} className={styles.infoBox}>
                            {calculatedUseHistoricalRates ? (
                                <>
                                    Based on historical inflation rates, ${amount.toLocaleString()} in {years} years is worth <b>{result}</b> in today's money.
                                </>
                            ) : (
                                <>
                                    With a {rate}% annual inflation rate, ${amount.toLocaleString()} in {years} years is worth <b>{result}</b> in today's money.
                                </>
                            )}
                        </Grid>
                    )}

                    {/* <Grid item xs={12} className={styles.sectionDivider}>
                        <Divider />
                    </Grid> */}

                    {/* <Grid item xs={12} className={styles.amountCard}>
                        <Grid container>
                            <Grid item xs={12} className={styles.amountLabel}>Amount</Grid>
                            <Grid item xs={12} className={styles.amountValue}>
                                {result ? result : "$0.00"}
                            </Grid>
                        </Grid>
                    </Grid> */}
                </Grid>
            </Grid>

            <Grid item xs={8.3} className={styles.rightScrollSection}>
                <Grid container gap={2}>
                    {showChart && amount !== undefined && years !== undefined && (
                        <>
                            <Grid item xs={12} className={styles.resultCard}>
                                <Grid container gap={1}>
                                    <Grid item xs={12} className={styles.sectionTitle}>
                                        Results
                                    </Grid>

                                    <Grid item xs={12}>
                                        {(() => {
                                            const displayYears = (showChart && years !== undefined) ? years : (inputYears ?? 0);
                                            const displayAmount = (showChart && amount !== undefined) ? amount : (inputAmount ?? 0);
                                            const resultNumber = result ? parseCurrencyToNumber(result) : 0;
                                            const percentLess = displayAmount > 0 ? ((1 - (resultNumber / displayAmount)) * 100) : 0;
                                            const annualLoss = displayYears > 0 ? (percentLess / displayYears) : 0;
                                            const realValue = displayAmount > 0 ? ((resultNumber / displayAmount) * 100) : 0;
                                            const inflationRateDisplay = (showChart && calculatedUseHistoricalRates) ? "Historical" : `${rate || inputRate || 0}%`;

                                            return (
                                                <Grid container className={styles.resultsRow}>
                                                    <Grid item className={styles.todayCard}>
                                                        <div className={styles.cardKicker}>Today</div>
                                                        <div className={styles.cardValue}>{formatMoney(displayAmount)}</div>
                                                        <div className={styles.cardSub}>Current Value</div>
                                                    </Grid>

                                                    <Grid item className={styles.arrowWrap}>
                                                        <div className={styles.arrowCircle}>
                                                            <ArrowForwardIcon sx={{ color: '#4A5565' }} />
                                                        </div>
                                                    </Grid>

                                                    <Grid item className={styles.futureCard}>
                                                        <div className={styles.cardKicker}>In {displayYears} Years</div>
                                                        <div className={styles.cardValue}>{result ? result : "$0"}</div>
                                                        <div className={styles.lossText}>{Math.max(0, percentLess).toFixed(1)}% less</div>
                                                    </Grid>

                                                    <Grid item xs className={styles.summaryCard}>
                                                        <Grid container className={styles.summaryGrid}>
                                                            <Grid item className={styles.summaryItem}>
                                                                <Grid container>
                                                                    <Grid item xs={12} className={styles.summaryLabel}>Inflation Rate</Grid>
                                                                    <Grid item xs={12} className={styles.summaryValue}>{inflationRateDisplay}</Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid className={styles.summaryDivider} />
                                                            <Grid item className={styles.summaryItem}>
                                                                <Grid container>
                                                                    <Grid item xs={12} className={styles.summaryLabel}>Time Period</Grid>
                                                                    <Grid item xs={12} className={styles.summaryValue}>{displayYears} years</Grid>
                                                                </Grid>
                                                            </Grid>
                                                            {/* <div className={styles.summaryDivider} />
                                                    <div className={styles.summaryItem}>
                                                        <div className={`${styles.summaryLabel} ${styles.good}`}>Real Value</div>
                                                        <div className={`${styles.summaryValue} ${styles.good}`}>{Math.max(0, realValue).toFixed(1)}%</div>
                                                    </div>
                                                    <div className={styles.summaryDivider} />
                                                    <div className={styles.summaryItem}>
                                                        <div className={`${styles.summaryLabel} ${styles.bad}`}>Annual Loss</div>
                                                        <div className={`${styles.summaryValue} ${styles.bad}`}>{Math.max(0, annualLoss).toFixed(2)}%</div>
                                                    </div> */}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })()}
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container gap={2}>
                                    <Grid item xs={12}>
                                        <FutureValueChart
                                            data={chartData}
                                            amount={amount}
                                            historicalData={historicalData}
                                            years={years}
                                            staticRate={parseFloat(rate)}
                                            useHistoricalRates={calculatedUseHistoricalRates}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <HistoricalCpiChart data={historicalData} />
                    </Grid>

                    <Grid item xs={12}>
                        <CpiCategoryData cpiData={cpiData} blsSyncDate={blsSyncDate} />
                    </Grid>
                </Grid>
            </Grid>

        </Grid>
    )
}

export default InflationCalculator